import * as React from 'react'
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    DialogActions,
    Dialog,
    DialogContent,
    DialogTitle,
    Typography,
    Switch,
    Collapse,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Slider,
    FormControlLabel,
    Checkbox,
    Grid,
    Button,
    IconButton
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import BarracksCastleConfigurator from './BarracksCastleConfigurator'
import KhanAttackConfigurator from './KhanAttackConfigurator'
import useAutoTranslate from '../hooks/useAutoTranslate'

const PLUGIN_FILTERS = {
    all: null,
    attack: new Set([
        'Attack Barrons (Great Empire)',
        'Attack Barrons (Fire Peaks)',
        'Attack Barrons (Glacier)',
        'Attack Barrons (Burning Sands)',
        'Attack Barrons (Outer Realms)',
        'Attack Beri Camps',
        'Attack Fortress (Fire Peaks)',
        'Attack Fortress (Everwinter Glacier)',
        'Attack Fortress (Burning Sands)',
        'Attack Aqua Forts',
        'Khan Attack',
        'Attack Nomad Camps',
        'Attack Samurai Camps',
        'Attack Berimond Kingdom',
        'Attack'
    ]),
    recruit: new Set([
        'Sam Shop',
        'Nomad Shop',
        'Hospital',
        'Hospital Healer',
        'Help',
        'Alliance Auto Help',
        'Grab Tools (MB)',
        'Flee (Experimental)',
        'Barracks',
        'Auto Recruit Tools',
        'Auto Craft Components',
        'Auto Craft Refinery'
    ]),
    alert: new Set([
        'Player Alert',
        'Alliance Player Alert'
    ]),
    other: new Set([
        'Auto Speed',
        'BattleLogger',
        'Feast',
        'Res Send',
        'Food Check',
        'Island Cargo Auto',
        'Native Villages',
        'Timer',
        'BtH',
        'Inventory Cleanup',
        'Equipment Seller'
    ])
}

const PLUGIN_FILTER_BUTTONS = [
    { key: 'all', label: 'All' },
    { key: 'attack', label: 'Attack' },
    { key: 'recruit', label: 'Recruit' },
    { key: 'alert', label: 'Alert' },
    { key: 'other', label: 'Other' }
]

export const getPluginManagerVisiblePlugins = plugins =>
    (Array.isArray(plugins) ? plugins : []).filter(plugin => !plugin?.hidden)

function getSelectionOption(option, fallbackValue) {
    if (option && typeof option === 'object' && !Array.isArray(option)) {
        return {
            label: option.label ?? String(option.value ?? fallbackValue),
            value: option.value ?? fallbackValue
        }
    }

    return {
        label: option,
        value: fallbackValue
    }
}

function normalizeAttackPresetStatusOption(option, fallbackIndex) {
    if (option && typeof option === 'object' && !Array.isArray(option)) {
        const fallbackValue = option.value ?? option.index ?? fallbackIndex
        return {
            label: option.label ?? `Preset ${Number(fallbackValue) + 1}`,
            value: option.value ?? option.index ?? fallbackIndex,
            isAvailable: option.isAvailable !== false,
            unavailableReason: String(option.unavailableReason ?? '').trim()
        }
    }

    return {
        label: String(option ?? `Preset ${Number(fallbackIndex) + 1}`),
        value: fallbackIndex,
        isAvailable: true,
        unavailableReason: ''
    }
}

function buildAttackPresetSelections(baseSelection, availableAttackPresets, currentValues) {
    const fallbackSelections = Array.isArray(baseSelection) ?
        baseSelection.map((option, index) => getSelectionOption(option, index)) :
        []
    const livePresetOptions = Array.isArray(availableAttackPresets) ?
        availableAttackPresets.map((option, index) => normalizeAttackPresetStatusOption(option, index)) :
        []

    if (livePresetOptions.length <= 0)
        return fallbackSelections

    const disabledOption = fallbackSelections.find(option => String(option.value ?? '') === '') ?? {
        label: 'Disabled',
        value: ''
    }
    const liveSelections = livePresetOptions
        .sort((left, right) => Number(left.value) - Number(right.value))
        .map(option => ({
            label:
                option.unavailableReason === 'locked' ? `${option.label} (Locked)` :
                    option.unavailableReason === 'empty' ? `${option.label} (Empty)` :
                        option.label,
            value: option.value
        }))
    const nextSelections = [disabledOption, ...liveSelections]
    const preservedValues = Array.isArray(currentValues) ? currentValues : [currentValues]

    preservedValues
        .filter(currentValue => ![undefined, null, ''].includes(currentValue))
        .forEach(currentValue => {
            if (nextSelections.some(option => String(option.value) === String(currentValue)))
                return

            const matchedLivePreset = livePresetOptions.find(option => String(option.value) === String(currentValue))
            const baseLabel =
                matchedLivePreset?.label ??
                (Number.isFinite(Number(currentValue)) ? `Preset ${Number(currentValue) + 1}` : `Preset ${currentValue}`)
            const suffix =
                matchedLivePreset?.unavailableReason === 'locked' ? ' (Locked)' :
                    matchedLivePreset?.unavailableReason === 'empty' ? ' (Empty)' :
                        ' (Unavailable)'

            nextSelections.push({
                label: `${baseLabel}${suffix}`,
                value: currentValue
            })
        })

    return nextSelections
}

function PluginCard({ data, userPlugins, onChange, channels, onSave, availableCastleTargets, availableAttackPresets, appLanguage = 'en', defaultExpanded = false, templatePlatform = 'gge' }) {
    userPlugins[data.key] ??= {}
    const [expanded, setExpanded] = React.useState(defaultExpanded)
    const [active, setActive] = React.useState(userPlugins[data.key]?.state)
    const [khanEditorOpen, setKhanEditorOpen] = React.useState(false)
    const attackPresetOption = data.pluginOptions?.find(option => option.key === 'attackPresetSlot')
    const khanConfigOption = data.pluginOptions?.find(option => option.type === 'KhanAttackConfig')
    const t = useAutoTranslate(appLanguage, [
        'Active',
        'Inactive',
        'Hide Settings',
        'Show Settings',
        'Open Khan Editor',
        'Open the full Khan configuration editor in a dedicated workspace.',
        `${data.name} Configuration Editor`,
        'Advanced Khan controls opened separately from the current plugin settings.',
        'Close',
        'Save Configuration',
        data.name,
        data.description
    ])

    const handleToggle = (e) => {
        e.stopPropagation() // Prevent expand when clicking toggle
        const newState = !active
        setActive(newState)
        userPlugins[data.key].state = newState
        onChange(userPlugins)
    }

    const hasOptions = data?.pluginOptions && data.pluginOptions.length > 0;
    const hasAttackTemplateOptions = Boolean(attackPresetOption)
    const hasKhanAttackConfigurator = Boolean(khanConfigOption)
    const effectivePluginOptions = (data.pluginOptions ?? []).map(option =>
        option.key === 'attackPresetSlot' ? {
            ...option,
            selection: buildAttackPresetSelections(
                option.selection,
                availableAttackPresets,
                [
                    userPlugins[data.key]?.attackPresetSlot,
                    userPlugins[data.key]?.attackTemplateBuilder?.presetSource
                ]
            )
        } :
            option
    )
    const inlineOptionKeys = React.useMemo(() =>
        hasAttackTemplateOptions ?
            new Set(['attackPresetSlot', 'attackPresetApplyMode']) :
            new Set(),
    [hasAttackTemplateOptions])
    const inlineOptions = effectivePluginOptions.filter(option => inlineOptionKeys.has(option.key))
    const stackedOptions = effectivePluginOptions.filter(option =>
        !inlineOptionKeys.has(option.key) &&
        option.type !== 'KhanAttackConfig')
    const handleSaveFromKhan = () => {
        const didSave = onSave?.({ closeAfterSave: false })

        if (didSave !== false)
            setKhanEditorOpen(false)
    }

    React.useEffect(() => {
        if (defaultExpanded)
            setExpanded(true)
    }, [defaultExpanded])

    return (
        <Card sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
        }}>
            <CardHeader
                action={
                    (!data.force) && (
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={Boolean(active)}
                                    onChange={handleToggle}
                                    color="secondary"
                                />
                            }
                            label={active ? t('Active') : t('Inactive')}
                            sx={{ marginRight: 0 }}
                        />
                    )
                }
                title={
                    <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem', fontWeight: 600, color: active ? 'primary.main' : 'text.primary' }}>
                        {t(data.name)}
                    </Typography>
                }
                subheader={
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                        {t(data.description)}
                    </Typography>
                }
                sx={{ paddingBottom: 1 }}
            />

            {hasOptions && (
                <Box sx={{ px: 2, pb: 1 }}>
                    <Button
                        size="small"
                        endIcon={expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                        onClick={() => setExpanded(!expanded)}
                        sx={{ color: 'text.secondary' }}
                    >
                        {expanded ? t('Hide Settings') : t('Show Settings')}
                    </Button>
                </Box>
            )}

            <Collapse in={expanded} timeout="auto" unmountOnExit>
                <CardContent sx={{ paddingTop: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {inlineOptions.length > 0 && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    alignItems: 'stretch',
                                    flexWrap: { xs: 'wrap', xl: 'nowrap' }
                                }}
                            >
                                {inlineOptions.map((option) => (
                                    <Box key={option.key} sx={{ flex: '1 1 220px', minWidth: 220 }}>
                                        <PluginOption
                                            pluginData={option}
                                            parentKey={data.key}
                                            userPlugins={userPlugins}
                                            onChange={onChange}
                                            channels={channels}
                                            availableCastleTargets={availableCastleTargets}
                                            appLanguage={appLanguage}
                                        />
                                    </Box>
                                ))}
                            </Box>
                        )}
                        {stackedOptions.map((option) => (
                            <PluginOption
                                key={option.key}
                                pluginData={option}
                                parentKey={data.key}
                                userPlugins={userPlugins}
                                onChange={onChange}
                                channels={channels}
                                availableCastleTargets={availableCastleTargets}
                                appLanguage={appLanguage}
                            />
                        ))}
                        {hasKhanAttackConfigurator && (
                            <Box
                                sx={{
                                    mt: stackedOptions.length > 0 ? 1 : 0,
                                    pt: stackedOptions.length > 0 ? 2 : 0,
                                    borderTop: stackedOptions.length > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.25
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    {t('Open the full Khan configuration editor in a dedicated workspace.')}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    startIcon={<OpenInFullRoundedIcon />}
                                    onClick={() => setKhanEditorOpen(true)}
                                >
                                    {t('Open Khan Editor')}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </CardContent>
            </Collapse>

            {hasKhanAttackConfigurator && (
                <Dialog
                    fullScreen
                    open={khanEditorOpen}
                    onClose={() => setKhanEditorOpen(false)}
                >
                    <DialogTitle
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            borderBottom: '1px solid rgba(255,255,255,0.08)'
                        }}
                    >
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700 }}>
                                {t(`${data.name} Configuration Editor`)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('Advanced Khan controls opened separately from the current plugin settings.')}
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setKhanEditorOpen(false)}>
                            <CloseRoundedIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            background: 'linear-gradient(180deg, rgba(11,16,26,0.98) 0%, rgba(19,29,47,0.98) 100%)',
                            p: { xs: 2, md: 3 }
                        }}
                    >
                        <KhanAttackConfigurator
                            parentKey={data.key}
                            userPlugins={userPlugins}
                            onChange={onChange}
                            appLanguage={appLanguage}
                            hideWorkspaceHeader
                            templatePlatform={templatePlatform}
                        />
                    </DialogContent>
                    <DialogActions
                        sx={{
                            px: { xs: 2, md: 3 },
                            py: 2,
                            borderTop: '1px solid rgba(255,255,255,0.08)',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Button variant="outlined" onClick={() => setKhanEditorOpen(false)}>
                            {t('Close')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveRoundedIcon />}
                            onClick={handleSaveFromKhan}
                        >
                            {t('Save Configuration')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Card>
    )
}

function PluginOption({ pluginData, parentKey, userPlugins, onChange, channels, availableCastleTargets, appLanguage = 'en' }) {
    // Ensure default value
    userPlugins[parentKey][pluginData.key] ??= pluginData.default
    const [value, setValue] = React.useState(userPlugins[parentKey][pluginData.key])
    const t = useAutoTranslate(appLanguage, [
        pluginData.label,
        'Complex Table Config',
        'Unknown Option'
    ])

    const handleChange = (newValue) => {
        userPlugins[parentKey][pluginData.key] = newValue
        setValue(newValue)
        onChange(userPlugins)
    }

    switch (pluginData.type) {
        case "Label":
            return <Typography variant="subtitle2" sx={{ color: 'primary.light', mt: 1 }}>{t(pluginData.label)}</Typography>

        case "Text":
            return (
                <TextField
                    label={t(pluginData.label)}
                    value={value || ''}
                    onChange={(e) => handleChange(e.target.value)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    sx={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                />
            )

        case "Checkbox":
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={Boolean(value)}
                            onChange={(_, checked) => handleChange(checked)}
                            sx={{ color: 'primary.main' }}
                        />
                    }
                    label={<Typography variant="body2">{t(pluginData.label)}</Typography>}
                />
            )

        case "Channel":
        case "Select":
            const isChannel = pluginData.type === "Channel"
            return (
                <FormControl fullWidth size="small">
                    <InputLabel>{t(pluginData.label)}</InputLabel>
                    <Select
                        value={value ?? ''}
                        label={t(pluginData.label)}
                        onChange={(e) => handleChange(e.target.value)}
                        sx={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
                    >
                        {isChannel
                            ? channels?.map((ch, i) => <MenuItem value={ch.id} key={`${pluginData.label}${i}`}>{ch.name}</MenuItem>)
                            : pluginData.selection.map((option, i) => {
                                const selection = getSelectionOption(option, i)
                                return <MenuItem value={selection.value} key={`${pluginData.key}-${selection.value}`}>{t(selection.label)}</MenuItem>
                            })
                        }
                    </Select>
                </FormControl>
            )

        case "Slider":
            return (
                <Box sx={{ px: 1 }}>
                    <Typography variant="caption" color="text.secondary">{t(pluginData.label)}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Slider
                            value={Number(value ?? 0)}
                            onChange={(_, val) => handleChange(val)}
                            valueLabelDisplay="auto"
                            size="small"
                        />
                        <Typography variant="body2" sx={{ minWidth: 30 }}>{value}%</Typography>
                    </Box>
                </Box>
            )

        case "Table":
            // Complex tables stay as tables, but styled lightly
            return (
                <Box sx={{ border: '1px solid rgba(255,255,255,0.1)', borderRadius: 1, p: 1, overflowX: 'auto' }}>
                    {/* Simplified placeholder for table logic for compactness */}
                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>{t(pluginData.label)}</Typography>
                    {/* For now, just indicating table support is needed, copying full logic might be verbose but necessary */}
                    <Typography variant="body2" color="warning.main">{t('Complex Table Config')}</Typography>
                </Box>
            )

        case "BarracksTargets":
            return (
                <BarracksCastleConfigurator
                    pluginData={pluginData}
                    parentKey={parentKey}
                    userPlugins={userPlugins}
                    onChange={onChange}
                    availableTargets={availableCastleTargets}
                    appLanguage={appLanguage}
                />
            )

        case "KhanAttackConfig":
            return null

        default:
            return <Typography color="error" variant="caption">{t('Unknown Option')}: {pluginData.type}</Typography>
    }
}

export default function PluginManager(props) {
    const userPlugins = props.userPlugins ?? {}
    const [activeFilter, setActiveFilter] = React.useState('all')
    const allPlugins = React.useMemo(() => getPluginManagerVisiblePlugins(props.plugins), [props.plugins])
    const t = useAutoTranslate(props.appLanguage ?? 'en', [
        'All',
        'Attack',
        'Recruit',
        'Alert',
        'Other',
        'No modules matched this category.'
    ])

    const pluginCounts = React.useMemo(() =>
        PLUGIN_FILTER_BUTTONS.reduce((counts, filter) => {
            const allowedNames = PLUGIN_FILTERS[filter.key]
            counts[filter.key] = allowedNames ?
                allPlugins.filter(plugin => allowedNames.has(plugin.name)).length :
                allPlugins.length
            return counts
        }, {}),
    [allPlugins])

    const filteredPlugins = React.useMemo(() => {
        const allowedNames = PLUGIN_FILTERS[activeFilter]
        if (!allowedNames)
            return allPlugins

        return allPlugins.filter(plugin => allowedNames.has(plugin.name))
    }, [activeFilter, allPlugins])

    const visiblePlugins = React.useMemo(() => {
        if (!props.activePluginKey)
            return filteredPlugins

        return filteredPlugins.filter(plugin => plugin.key === props.activePluginKey)
    }, [filteredPlugins, props.activePluginKey])

    return (
        <Box sx={{ p: 2 }}>
            {!props.hideFilters && (
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        mb: 2.5
                    }}
                >
                    {PLUGIN_FILTER_BUTTONS.map(filter => (
                        <Button
                            key={filter.key}
                            variant={activeFilter === filter.key ? 'contained' : 'outlined'}
                            color={activeFilter === filter.key ? 'primary' : 'inherit'}
                            onClick={() => setActiveFilter(filter.key)}
                            sx={{
                                minWidth: 120,
                                justifyContent: 'space-between',
                                px: 2
                            }}
                        >
                            {`${t(filter.label)} (${pluginCounts[filter.key] ?? 0})`}
                        </Button>
                    ))}
                </Box>
            )}

            <Grid container spacing={2}>
                {visiblePlugins.map((plugin) => {
                    return (
                        <Grid item xs={12} md={props.singleColumn ? 12 : 6} lg={props.singleColumn ? 12 : 4} key={plugin.key}>
                            <PluginCard
                                data={plugin}
                                userPlugins={userPlugins}
                                onChange={props.onChange}
                                channels={props.channels}
                                selectedUser={props.selectedUser}
                                ws={props.ws}
                                onSave={props.onSave}
                                availableCastleTargets={props.availableCastleTargets}
                                availableAttackPresets={props.availableAttackPresets}
                                availableAttackTemplateInventory={props.availableAttackTemplateInventory}
                                appLanguage={props.appLanguage}
                                defaultExpanded={Boolean(props.defaultExpandedAll)}
                                templatePlatform={props.templatePlatform ?? props.selectedUser?.platform}
                            />
                        </Grid>
                    )
                })}
            </Grid>
            {visiblePlugins.length === 0 && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid rgba(255,255,255,0.08)',
                        backgroundColor: 'rgba(255,255,255,0.03)'
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        {t('No modules matched this category.')}
                    </Typography>
                </Box>
            )}
        </Box>
    )
}
